<template>
    <div class="px-6 pb-6 space-y-6">
        <!-- Warning Sections -->
        <div class="space-y-4">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-start gap-3">
                    <div>
                        <h3 class="font-semibold text-red-900 mb-2 font-inter">This action cannot be undone!</h3>
                        <p class="text-red-900 text-sm font-inter">
                            Deleting your account will permanently remove all your data, including:
                        </p>
                        <ul class="text-red-900 text-sm my-2 space-y-1 font-inter">
                            <li>• Personal information and settings</li>
                            <li>• Exposure reports and scan history</li>
                            <li>• Privacy preferences and opt-outs</li>
                            <li>• Account credentials and access</li>
                        </ul>
                        <span class="font-semibold text-red-900 font-inter">
                            Once your account is deleted, you will lose access to all Privacy Bee services and your data cannot be
                            recovered.
                        </span>
                    </div>
                </div>
            </div>

        </div>

        <!-- Confirmation Input -->
        <div class="space-y-2 mt-2 w-full">
            <label for="delete-confirmation" class="block text-sm font-normal text-zinc-900 font-inter"> To confirm deletion, type DELETE in the field below. </label>
            <PrimeInputText
                id="delete-confirmation"
                v-model="deleteConfirmation"
                placeholder="DELETE"
                :class="{ 'border-red-500': deleteConfirmation && deleteConfirmation !== 'DELETE' }"
                :disabled="isDeleting"
                class="w-full"
            />
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 pt-4">
            <PrimeButton label="Cancel" variant="secondary" @click="$emit('closeDialog')" :disabled="isDeleting" />
            <PrimeButton
                label="Delete Account"
                class="bg-red-600 hover:bg-red-700 text-white"
                @click="confirmDelete"
                :disabled="deleteConfirmation !== 'DELETE' || isDeleting"
                :loading="isDeleting"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { usePbApi } from '~/composables/api';
import { useAuthStore } from '~/stores/auth';

const auth = useAuthStore();
const { showSuccess, showError } = useGlobalToast();

// Reactive variables
const deleteConfirmation = ref('');
const isDeleting = ref(false);

// Methods
const confirmDelete = async () => {
    if (deleteConfirmation.value !== 'DELETE') {
        return;
    }

    isDeleting.value = true;

    try {
        const { status, message } = await usePbApi('me', {
            method: 'DELETE',
        });

        if (status === 204) {
            // Account deleted successfully
            showSuccess('Success', 'Account deleted successfully');

            // Clear auth state and redirect to login
            await auth.logout();
            await navigateTo('/auth/login');
        } else {
            throw new Error(message || 'Failed to delete account');
        }
    } catch (error: any) {
        console.error('Delete account error:', error);
        showError('Error', error.message || 'Failed to delete account. Please try again.');
    } finally {
        isDeleting.value = false;
    }
};

defineEmits<{
    closeDialog: [];
}>();
</script>